import axios from "axios";
import { StorageBox } from "./stroage";
import { FUEL_MS_URL } from "./constants";
import { RouteConstants } from "./RouteConstants";

// Refresh a little early so a request never goes out with a token that
// expires mid-flight.
const EXPIRY_SKEW_MS = 30 * 1000;

/**
 * Maps an /auth/v2/* success payload (login, register-with-password, or
 * otp/verify) into the `{ token, user }` shape the rest of the app expects
 * from the legacy /auth/login response, and persists it the same way.
 */
export const persistAuthV2Session = (payload: any) => {
  StorageBox.saveAccessToken(payload?.access_token);
  StorageBox.saveRefreshToken(payload?.refresh_token);
  StorageBox.saveTokenExpiry(payload?.access_token_expires_at);
  saveSessionUser(payload?.user, payload?.access_token);
};

/** Updates the stored user after a profile change, keeping the token. */
export const saveSessionUser = (user: any, token?: string) => {
  const current = StorageBox.retrieveUserData();
  const fullName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(" ");

  StorageBox.saveUserData({
    token: token ?? current?.token,
    user: {
      uid: user?.external_id,
      name: fullName,
      first_name: user?.first_name,
      last_name: user?.last_name,
      phone: user?.phone,
      email: user?.email,
    },
  });
};

export const isAccessTokenExpiring = () => {
  const expiresAt = StorageBox.getTokenExpiry();
  if (!expiresAt) return false;
  return Date.parse(expiresAt) - EXPIRY_SKEW_MS <= Date.now();
};

/** A session the app can still use: a live token, or one we can refresh. */
export const hasUsableSession = () => {
  if (!StorageBox.getAccessToken()) return false;
  return !isAccessTokenExpiring() || !!StorageBox.getRefreshToken();
};

/** Swaps the refresh token for a new session. Returns the new access token, or null. */
export const refreshAuthV2Session = async (): Promise<string | null> => {
  const refreshToken = StorageBox.getRefreshToken();
  if (!refreshToken) return null;
  try {
    const response = await axios.post(
      FUEL_MS_URL + RouteConstants.REFRESH_TOKEN,
      { refresh_token: refreshToken },
    );
    const payload = response?.data?.payload;
    if (payload?.status !== "OK") return null;
    persistAuthV2Session(payload);
    return payload.access_token;
  } catch {
    return null;
  }
};

/** Clears the session and sends the user to sign in. */
export const endSession = () => {
  StorageBox.clearStorage();
  if (window.location.pathname !== "/signin") {
    window.location.replace("/signin");
  }
};

/** Revokes the refresh token server-side, then clears the session. */
export const logout = async () => {
  const refreshToken = StorageBox.getRefreshToken();
  if (refreshToken) {
    try {
      await axios.post(FUEL_MS_URL + RouteConstants.LOGOUT, {
        refresh_token: refreshToken,
      });
    } catch {
      // Already invalid server-side — nothing more to do.
    }
  }
  endSession();
};
